import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EyeOff, Settings } from "lucide-react";
import { MarkdownRenderer } from "./MarkdownRenderer";
import {
  OhmsLawDiagram,
  ResistorDiagram,
  CurrentDiagram,
  PowerDiagram,
  VoltageDividerDiagram,
  RCCircuitDiagram,
  RLCircuitDiagram,
  TheveninDiagram,
  CapacitorCombinationDiagram,
  InductorCombinationDiagram
} from "./visualizer/circuit";
import type { IQuestion } from "@/types/question";

interface Props {
  question: IQuestion;
  onNext: () => void;
  onSettingsClick?: () => void;
}

export function QuestionDisplay({ question, onNext, onSettingsClick }: Props) {
  const [showAnswer, setShowAnswer] = useState(false);
  const [title, content] = splitQuestionText(question.questionMarkdown);

  const renderVisualizer = () => {
    if (!question.metadata || question.topic !== "circuitanalysis") {
      return <br />;
    }

    switch (question.type) {
      case "ohmslaw":
        // Map subtype to solveFor
        let solveFor: 'current' | 'voltage' | 'resistance' = 'current';
        if (question.metadata.subtype === 'solveForVoltage') solveFor = 'voltage';
        if (question.metadata.subtype === 'solveForResistance') solveFor = 'resistance';

        return (
          <div className="my-4">
            <OhmsLawDiagram
              voltage={question.metadata.voltage}
              resistance={question.metadata.resistance}
              current={question.metadata.current}
              solveFor={solveFor}
            />
          </div>
        );

      case "resistors":
        return (
          <div className="my-4">
            <ResistorDiagram
              resistors={question.metadata.resistors}
              isSeries={question.metadata.isSeries}
              equivalent={question.metadata.equivalent}
            />
          </div>
        );

      case "current":
        return (
          <div className="my-4">
            <CurrentDiagram
              variant={question.metadata.variant}
              unknownIndex={question.metadata.unknownIndex}
              currents={question.metadata.currents}
              directions={question.metadata.directions}
              totalCurrent={question.metadata.totalCurrent}
              branchCurrents={question.metadata.branchCurrents}
              r1={question.metadata.r1}
              r2={question.metadata.r2}
              i1={question.metadata.i1}
              i2={question.metadata.i2}
            />
          </div>
        );

      case "power": {
        let powerSolveFor: 'current' | 'voltage' | 'resistance' | 'power' = 'power';
        if (question.metadata.subtype === 'solveForCurrent') powerSolveFor = 'current';
        if (question.metadata.subtype === 'solveForVoltage') powerSolveFor = 'voltage';
        if (question.metadata.subtype === 'solveForResistance') powerSolveFor = 'resistance';
        return (
          <div className="my-4">
            <PowerDiagram
              voltage={question.metadata.voltage}
              resistance={question.metadata.resistance}
              current={question.metadata.current}
              power={question.metadata.power}
              solveFor={powerSolveFor}
            />
          </div>
        );
      }

      case "voltagedivider": {
        let vdSolveFor: 'vIn' | 'r1' | 'r2' | 'vOut' = 'vOut';
        if (question.metadata.subtype === 'findVin') vdSolveFor = 'vIn';
        if (question.metadata.subtype === 'findR1') vdSolveFor = 'r1';
        if (question.metadata.subtype === 'findR2') vdSolveFor = 'r2';
        return (
          <div className="my-4">
            <VoltageDividerDiagram
              vIn={question.metadata.vin}
              r1={question.metadata.r1}
              r2={question.metadata.r2}
              vOut={question.metadata.vout}
              solveFor={vdSolveFor}
            />
          </div>
        );
      }

      case "capacitor": {
        const subtype = question.metadata.subtype as string;

        // RC circuit subtypes - show series RC diagram
        if (['timeConstant', 'chargingVoltage', 'dischargingVoltage'].includes(subtype)) {
          return (
            <div className="my-4">
              <RCCircuitDiagram
                voltage={question.metadata.vs || question.metadata.voltage || 12}
                resistance={question.metadata.resistance}
                capacitance={question.metadata.capacitance}
              />
            </div>
          );
        }

        // Combination subtypes - show series/parallel capacitor diagram
        if (['seriesCapacitors', 'parallelCapacitors'].includes(subtype)) {
          return (
            <div className="my-4">
              <CapacitorCombinationDiagram
                capacitors={question.metadata.capacitors}
                equivalent={question.metadata.equivalent}
                isSeries={question.metadata.isSeries}
              />
            </div>
          );
        }

        // storedEnergy - no circuit diagram (just equation)
        return <br />;
      }

      case "inductor": {
        const subtype = question.metadata.subtype as string;

        // RL circuit subtypes - show series RL diagram
        if (['timeConstant', 'currentGrowth', 'currentDecay'].includes(subtype)) {
          return (
            <div className="my-4">
              <RLCircuitDiagram
                voltage={question.metadata.vs || question.metadata.voltage || 12}
                resistance={question.metadata.resistance}
                inductance={question.metadata.inductance}
              />
            </div>
          );
        }

        // Combination subtypes - show series/parallel inductor diagram
        if (['seriesInductors', 'parallelInductors'].includes(subtype)) {
          return (
            <div className="my-4">
              <InductorCombinationDiagram
                inductors={question.metadata.inductors}
                equivalent={question.metadata.equivalent}
                isSeries={question.metadata.isSeries}
              />
            </div>
          );
        }

        // storedEnergy - no circuit diagram (just equation)
        return <br />;
      }

      case "thevenin":
        return (
          <div className="my-4">
            <TheveninDiagram
              vTh={question.metadata.vth}
              rTh={question.metadata.rth}
              loadResistance={question.metadata.rl || question.metadata.loadResistance}
              showEquivalent={true}
            />
          </div>
        );

      default:
        return <br />;
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mt-2 sm:mt-6">
        <div className="prose max-w-none">
          <MarkdownRenderer content={title} />
        </div>
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        )}
      </div>

      {renderVisualizer()}

      <div className="prose max-w-none tracking-wide">
        <MarkdownRenderer content={content} />
      </div>

      <div className="flex gap-6 mt-8">
        <button
          onClick={() => setShowAnswer(!showAnswer)}
          className="text-blue-600! hover:text-blue-800 hover:underline hover:cursor-pointer min-w-[100px] flex items-center"
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={showAnswer ? "hide" : "show"}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="block"
            >
              {showAnswer ? "Hide Answer" : "Show Answer"}
            </motion.span>
          </AnimatePresence>
        </button>
        <button onClick={onNext} className="text-gray-600 hover:text-gray-800 hover:underline hover:cursor-pointer">
          Next Question
        </button>
      </div>

      <div className="relative mt-8">
        <motion.div
          initial={{ opacity: 0, filter: "blur(10px)" }}
          animate={{
            opacity: showAnswer ? 1 : 0,
            filter: showAnswer ? "blur(0px)" : "blur(10px)"
          }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className={`border-dashed border-gray-300 border-2 py-4 px-4 tracking-wide rounded-md ${!showAnswer ? "select-none pointer-events-none bg-gray-50/30" : ""
            }`}
        >
          <MarkdownRenderer content={question.answerMarkdown} />
        </motion.div>

        {!showAnswer && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 select-none pointer-events-none gap-2">
            <EyeOff className="w-5 h-5 opacity-50" />
            <span className="text-sm font-medium opacity-70">Answer Hidden</span>
          </div>
        )}
      </div>
    </>
  );
}

function splitQuestionText(text: string): [string, string] {
  const [_, title, ...content] = text.split("\n");
  return [title, content.join("\n")];
}
